/**
 * Onboarding funnel events (PIVOT-299 acceptance: "every onboarding step
 * emits one funnel event"). `pages/Onboarding.tsx` calls `recordFunnelStep`
 * once per step it renders, so drop-off across intent/consent/upload/
 * interview/match-setup/review/policy-review/scoring is visible without
 * instrumenting every component by hand.
 *
 * No backend sink exists yet — none of this ticket's scoped files add one (a
 * real event pipeline needs its own table/route, which is a separate
 * ticket's job; `lib/` itself owns no network call either way — frontend/
 * CLAUDE.md §2). Console-only used to be the whole story here (PIVOT-299
 * review: a broken/closed devtools panel meant nothing survived to be
 * inspected at all — "emits an event" is not "a system receives or retains
 * it"), so the default sink below also appends to a small capped log in
 * `localStorage`: still this browser's own copy, not a server's, but a real
 * retained record a future sink or a support session can read back rather
 * than one that vanished the moment the console scrolled. `emit` is the one
 * seam that real sink replaces.
 */

export type FunnelStep =
  | 'intent'
  | 'consent'
  | 'declined'
  | 'upload'
  | 'interview'
  | 'match-setup'
  | 'constraints'
  | 'finalizing'
  | 'review'
  | 'policy-review'
  | 'scoring'

/** One retained event: the step and when it fired, so the log stays legible
 * once more than one onboarding attempt has written into it. */
export interface FunnelLogEntry {
  step: FunnelStep
  at: string
}

/** Exported so a future backend sink (or a support session, or this
 * module's own test) can read the same key back without duplicating it. */
export const FUNNEL_LOG_STORAGE_KEY = 'pivot.onboarding.funnel_log'

/** Oldest entries roll off first — this is a rolling window for diagnosing a
 * drop-off, not a growing history, and one onboarding attempt is at most 11
 * steps. */
const FUNNEL_LOG_LIMIT = 50

/** Appends one entry to the capped, `localStorage`-backed log.
 *
 * `localStorage` can throw on its own (a private window, a browser set to
 * block site data, a full quota) with no relation to whether the step itself
 * is real, so this degrades to a no-op on failure rather than take
 * onboarding down with it — the same contract `recordFunnelStep` promises
 * its own caller, kept one layer in.
 */
function appendToRetainedLog(step: FunnelStep): void {
  try {
    const raw = window.localStorage.getItem(FUNNEL_LOG_STORAGE_KEY)
    const existing: FunnelLogEntry[] = raw ? (JSON.parse(raw) as FunnelLogEntry[]) : []
    const updated = [...existing, { step, at: new Date().toISOString() }].slice(
      -FUNNEL_LOG_LIMIT,
    )
    window.localStorage.setItem(FUNNEL_LOG_STORAGE_KEY, JSON.stringify(updated))
  } catch {
    // See this function's docstring.
  }
}

let emit: (step: FunnelStep) => void = (step) => {
  // eslint-disable-next-line no-console -- inspectable during this flag's rollout; see module docstring
  console.info(`[funnel] onboarding.${step}`)
  appendToRetainedLog(step)
}

/** Fires the one funnel event for this step. Never throws — a broken sink
 * must not be able to break onboarding itself. */
export function recordFunnelStep(step: FunnelStep): void {
  try {
    emit(step)
  } catch {
    // A sink's own failure is not this candidate's problem.
  }
}

/** Test-only: swap the sink so a test can assert what fired without console
 * noise, and restore it afterwards. */
export function setFunnelSinkForTest(sink: (step: FunnelStep) => void): () => void {
  const previous = emit
  emit = sink
  return () => {
    emit = previous
  }
}
